import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Download, Print } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import InvoicePrint from './InvoicePrint';

interface InvoicePrintDialogProps {
  open: boolean;
  onClose: () => void;
  appointment: any;
  orders: any[];
  hospitalInfo: {
    logoUrl: string;
    name: string;
    address: string;
    phone?: string;
    email?: string;
  };
}

const InvoicePrintDialog: React.FC<InvoicePrintDialogProps> = ({ open, onClose, appointment, orders, hospitalInfo }) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (!printRef.current) return;
    const printContents = printRef.current.innerHTML;
    const win = window.open('', '', 'width=900,height=650');
    if (!win) return;
    win.document.write('<html><head><title>Invoice</title>');
    win.document.write('<style>body{margin:0;font-family:sans-serif;}@media print{@page{size:A4;margin:16mm;}}</style>');
    win.document.write('</head><body>');
    win.document.write(printContents);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    
    try {
      // Create canvas from the invoice element
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename with appointment number and date
      const appointmentNumber = appointment?.appointmentNumber || 'INV';
      const date = new Date().toISOString().split('T')[0];
      const filename = `Invoice_${appointmentNumber}_${date}.pdf`;
      
      // Download the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Print Invoice</DialogTitle>
      <DialogContent>
        <div ref={printRef}>
          <InvoicePrint appointment={appointment} orders={orders} hospitalInfo={hospitalInfo} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button 
          onClick={handleDownloadPDF} 
          variant="outlined" 
          color="primary" 
          startIcon={<Download />}
          sx={{ mr: 1 }}
        >
          Download PDF
        </Button>
        <Button 
          onClick={handlePrint} 
          variant="contained" 
          color="primary" 
          startIcon={<Print />}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePrintDialog;
